/* ================================================================
   Cart Drawer — Shell controller
   Custom element <cart-drawer> extending Drawer (ModalDialog)
   ================================================================ */
(function () {
  'use strict';

  /* ── Inject CSS to hide chat widgets when cart drawer is open ── */
  if (!document.getElementById('cart-chat-hide-style')) {
    var style = document.createElement('style');
    style.id = 'cart-chat-hide-style';
    style.textContent =
      'body.cart-drawer-open .chat-app,' +
      'body.cart-drawer-open #shopify-chat,' +
      'body.cart-drawer-open [class*="shopify-chat"],' +
      'body.cart-drawer-open [id*="shopify-chat"],' +
      'body.cart-drawer-open .gorgias-chat-container,' +
      'body.cart-drawer-open #gorgias-chat-container{' +
      'display:none!important;visibility:hidden!important;' +
      'opacity:0!important;pointer-events:none!important}';
    document.head.appendChild(style);
  }

  /* ── Helpers ── */

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    var dollars = (Number(cents) / 100).toFixed(2);
    return '$' + dollars;
  }

  function trackEvent(action, label, metadata) {
    if (window.DYCAnalytics && typeof window.DYCAnalytics.trackCustom === 'function') {
      window.DYCAnalytics.trackCustom('cart', action, label, metadata);
    }
  }

  function initLucide() {
    if (typeof lucide !== 'undefined' && typeof lucide.createIcons === 'function') {
      lucide.createIcons();
    }
  }

  /* ── Define custom element ── */

  if (!customElements.get('cart-drawer')) {
    customElements.define(
      'cart-drawer',
      class CartDrawer extends Drawer {
        constructor() {
          super();

          this._activeTab = 'cart';
          this._touchStartY = 0;
          this._touchStartTime = 0;
          this._swipeDelta = 0;
          this._mutationDebounce = null;
        }

        connectedCallback() {
          this._bindTabs();
          this._bindSavedActions();
          this._bindWishlistEvents();
          this._bindCartUpdateEvents();
          this._bindCartLinkInterception();
          this._observeCartDetails();
          this._bindCartActions();
          this._bindWingToggle();
          this._updateSavedCount();
          this._patchSectionUpdate();
          var self = this;
          setTimeout(function() { self._updateSavedCount(); }, 500);
          document.addEventListener('DOMContentLoaded', function() { self._updateSavedCount(); });
        }

        /* ────────────────────────────────────────────────────────
           Show / Hide overrides
           ──────────────────────────────────────────────────────── */

        show(opener) {
          super.show(opener);

          document.body.classList.add('cart-drawer-open');

          /* Mobile bottom-sheet mode */
          if (window.innerWidth <= 749) {
            this.classList.add('cart-drawer--mobile-sheet');
            this._bindSwipeToDismiss();
          } else {
            this.classList.remove('cart-drawer--mobile-sheet');
          }

          /* Desktop wing: start hidden, user opens via toggle button */
          if (window.innerWidth > 1024) {
            var wing = this.querySelector('.cart-upsells');
            this._wingRevealed = false;
            if (wing) {
              wing.classList.remove('wing-animate', 'wing-revealed');
              this._syncWingWithCart();
            }
          }

          /* Recommendation widget lifecycle */
          var rec = this.querySelector('recommendation-by-history');
          if (rec && typeof rec.showContent === 'function') {
            rec.showContent();
          }

          /* Lucide icons inside drawer */
          initLucide();

          /* Track drawer open with cart value context */
          var self = this;
          fetch('/cart.js?_fd=0', { credentials: 'same-origin' })
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              trackEvent('drawer_opened', self._activeTab, {
                item_count: cart.item_count,
                total_cents: cart.total_price,
                total: (cart.total_price / 100).toFixed(2)
              });
            })
            .catch(function() {
              trackEvent('drawer_opened', self._activeTab);
            });
        }

        hide() {
          super.hide();
          trackEvent('drawer_closed', '');

          document.body.classList.remove('cart-drawer-open');

          /* Reset wing animation so it plays again next open */
          this._wingRevealed = false;
          var wing = this.querySelector('.cart-upsells');
          if (wing) {
            wing.classList.remove('wing-revealed');
            wing.classList.remove('wing-animate');
          }
          var trigger = this.querySelector('.cart-wing-trigger');
          if (trigger) trigger.classList.remove('wing-open');

          var rec = this.querySelector('recommendation-by-history');
          if (rec && typeof rec.abortFetch === 'function') {
            rec.abortFetch();
          }

          /* Clean up mobile sheet class */
          this.classList.remove('cart-drawer--mobile-sheet');

          /* Reset panel transform from swipe */
          var panel = this.querySelector('.cart-drawer-panel');
          if (panel) {
            panel.style.transform = '';
            panel.style.transition = '';
          }
        }

        /* ────────────────────────────────────────────────────────
           Tab switching
           ──────────────────────────────────────────────────────── */

        _bindTabs() {
          var self = this;
          var tabs = this.querySelectorAll('.cart-tab');

          tabs.forEach(function (tab) {
            tab.addEventListener('click', function () {
              var tabName = tab.getAttribute('data-tab');
              if (tabName === self._activeTab) return;

              self._activeTab = tabName;

              /* Toggle active state on buttons */
              tabs.forEach(function (t) {
                var isActive = t.getAttribute('data-tab') === tabName;
                t.classList.toggle('cart-tab--active', isActive);
                t.setAttribute('aria-selected', isActive ? 'true' : 'false');
              });

              /* Toggle content panels */
              var cartPanel = self.querySelector('[data-tab-content="cart"]');
              var savedPanel = self.querySelector('[data-tab-content="saved"]');

              if (cartPanel) cartPanel.style.display = tabName === 'cart' ? '' : 'none';
              if (savedPanel) savedPanel.style.display = tabName === 'saved' ? 'flex' : 'none';

              /* Render saved items when switching to saved tab */
              if (tabName === 'saved') {
                self._renderSavedItems();
              }

              trackEvent('tab_switched', tabName);
            });
          });
        }

        /* ────────────────────────────────────────────────────────
           Saved items rendering
           ──────────────────────────────────────────────────────── */

        _renderSavedItems() {
          if (!window.DYCWishlist) return;

          var items = window.DYCWishlist.getSavedItems();
          var list = this.querySelector('.saved-items-list');
          var empty = this.querySelector('.saved-empty-state');
          var footer = this.querySelector('.saved-tab-footer');
          var hint = this.querySelector('.saved-hint');

          if (!list) return;

          if (items.length === 0) {
            list.innerHTML = '';
            if (empty) empty.style.display = 'flex';
            if (footer) footer.style.display = 'none';
            if (hint) hint.style.display = 'none';
            initLucide();
            return;
          }

          if (empty) empty.style.display = 'none';
          if (footer) footer.style.display = '';
          if (hint) hint.style.display = '';

          var html = '';
          for (var i = 0; i < items.length; i++) {
            var item = items[i];
            var img = item.image || '';
            var title = item.title || 'Product';
            var variant = item.variantTitle || '';
            var price = item.price ? formatMoney(item.price) : '';
            var compareAt = item.compareAtPrice ? formatMoney(item.compareAtPrice) : '';
            var vid = item.variantId || '';

            html += '<div class="saved-item" data-variant-id="' + vid + '">';
            html += '<div class="saved-item-image">';
            if (img) {
              html += '<img src="' + img + '" alt="' + title.replace(/"/g, '&quot;') + '" width="72" height="72" loading="lazy">';
            } else {
              html += '<div class="saved-item-placeholder"><i data-lucide="image" style="width:24px;height:24px;opacity:.3"></i></div>';
            }
            html += '</div>';
            html += '<div class="saved-item-info">';
            html += '<p class="saved-item-title">' + title + '</p>';
            if (variant) {
              html += '<p class="saved-item-variant">' + variant + '</p>';
            }
            html += '<div class="saved-item-price">';
            if (compareAt && item.compareAtPrice > item.price) {
              html += '<s class="saved-item-compare">' + compareAt + '</s> ';
            }
            if (price) {
              html += '<span class="saved-item-current">' + price + '</span>';
            }
            html += '</div>';
            html += '</div>';
            html += '<div class="saved-item-actions">';
            html += '<button type="button" class="saved-item-heart" data-action="remove-saved" data-variant-id="' + vid + '" aria-label="Remove saved item">';
            html += '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>';
            html += '</button>';
            html += '<button type="button" class="saved-item-move button button--sm" data-action="move-to-cart" data-variant-id="' + vid + '">Move to Cart</button>';
            html += '</div>';
            html += '</div>';
          }

          list.innerHTML = html;
          initLucide();
        }

        _bindSavedActions() {
          var self = this;
          var savedPanel = this.querySelector('[data-tab-content="saved"]');
          if (!savedPanel) return;

          /* Delegate clicks on saved items list and footer */
          savedPanel.addEventListener('click', function (e) {
            if (!window.DYCWishlist) return;

            var removeBtn = e.target.closest('[data-action="remove-saved"]');
            if (removeBtn) {
              var vid = parseInt(removeBtn.getAttribute('data-variant-id'));
              if (vid) {
                window.DYCWishlist.removeItem(vid);
                self._renderSavedItems();
                self._updateSavedCount();
                trackEvent('saved_item_removed', vid);
              }
              return;
            }

            var moveBtn = e.target.closest('[data-action="move-to-cart"]');
            if (moveBtn) {
              var mvid = parseInt(moveBtn.getAttribute('data-variant-id'));
              if (mvid) {
                moveBtn.disabled = true;
                moveBtn.textContent = 'Adding...';
                /* Immediately hide the item in the DOM for instant feedback */
                var itemEl = moveBtn.closest('.saved-item');
                if (itemEl) itemEl.style.display = 'none';

                window.DYCWishlist.moveToCart(mvid).then(function () {
                  /* Ensure item is removed from localStorage (safety net) */
                  if (window.DYCWishlist.isItemSaved(mvid)) {
                    window.DYCWishlist.removeItem(mvid);
                  }
                  self._renderSavedItems();
                  self._updateSavedCount();
                  self._refreshSections();
                  /* Switch to cart tab after moving */
                  var cartTab = self.querySelector('[data-tab="cart"]');
                  if (cartTab) cartTab.click();
                });
                trackEvent('saved_move_to_cart', mvid);
              }
              return;
            }

            var addAllBtn = e.target.closest('.saved-add-all-btn');
            if (addAllBtn) {
              addAllBtn.disabled = true;
              addAllBtn.textContent = 'Adding...';
              window.DYCWishlist.moveAllToCart().then(function () {
                addAllBtn.disabled = false;
                addAllBtn.textContent = 'Add All Saved to Cart';
                self._renderSavedItems();
                self._updateSavedCount();
                self._refreshSections();
              });
              trackEvent('saved_add_all_to_cart', '');
              return;
            }
          });
        }

        /* ────────────────────────────────────────────────────────
           Wishlist update listener
           ──────────────────────────────────────────────────────── */

        _bindWishlistEvents() {
          var self = this;
          document.addEventListener('dyc:wishlist-update', function () {
            self._updateSavedCount();
            if (self._activeTab === 'saved') {
              self._renderSavedItems();
            }
          });
        }

        _updateSavedCount() {
          var badge = this.querySelector('.saved-tab-count');
          if (!badge) return;
          var count = (window.DYCWishlist && typeof window.DYCWishlist.getCount === 'function')
            ? window.DYCWishlist.getCount()
            : 0;
          badge.textContent = count;
        }

        /* ────────────────────────────────────────────────────────
           Cart update listener — keep cart tab count in sync
           ──────────────────────────────────────────────────────── */

        _bindCartUpdateEvents() {
          var self = this;
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
              var badge = self.querySelector('.cart-tab-count');
              if (!badge) return;

              /* cartData comes from the section re-render response */
              var data = event && event.cartData;
              if (data && typeof data.item_count !== 'undefined') {
                badge.textContent = data.item_count;
              } else {
                /* Fallback: fetch cart count from Shopify */
                fetch('/cart.js?_fd=0', { credentials: 'same-origin' })
                  .then(function (r) { return r.json(); })
                  .then(function (cart) {
                    badge.textContent = cart.item_count;
                  })
                  .catch(function () { /* ignore */ });
              }
            });
          }
        }

        /* ────────────────────────────────────────────────────────
           /cart link interception
           ──────────────────────────────────────────────────────── */

        _bindCartLinkInterception() {
          var self = this;
          document.addEventListener('click', function (e) {
            var link = e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
            if (link && !e.defaultPrevented) {
              e.preventDefault();
              self.show();
            }
          }, true);
        }

        /* ────────────────────────────────────────────────────────
           MutationObserver — re-init Lucide after section re-render
           ──────────────────────────────────────────────────────── */

        _observeCartDetails() {
          var self = this;
          var target = this.querySelector('#Cart-Drawer-Details');
          if (!target) return;

          var observer = new MutationObserver(function () {
            clearTimeout(self._mutationDebounce);
            self._mutationDebounce = setTimeout(function () {
              initLucide();
              /* Keep desktop wing visible after section re-render
                 Only re-apply if wing was already revealed (wing-animate present) */
              if (window.innerWidth > 1024 && self.hasAttribute('open') && self._wingRevealed) {
                var wing = self.querySelector('.cart-upsells');
                if (wing) {
                  wing.classList.add('wing-revealed');
                  wing.classList.add('wing-animate');
                }
              }
            }, 50);
          });

          observer.observe(target, { childList: true, subtree: true });
        }

        /* ────────────────────────────────────────────────────────
           Monkey-patch SectionDynamicUpdate to preserve desktop wing
           This intercepts ALL section updates (cart.js, product-form.js,
           accessories-drawer.js, mcc.js, etc.) so the wing DOM element
           survives cart re-renders without flashing.
           ──────────────────────────────────────────────────────── */

        _patchSectionUpdate() {
          if (typeof SectionDynamicUpdate === 'undefined') return;
          if (SectionDynamicUpdate._wingPatched) return;

          var drawerEl = this;
          var origUpdate = SectionDynamicUpdate.updateSections;
          SectionDynamicUpdate.updateSections = function(sections, responseSections) {
            /* On desktop, save the wing before any Cart-Drawer-Details replacement */
            var savedWing = null;
            if (window.innerWidth > 1024) {
              var existing = document.getElementById('Cart-Drawer-Details');
              if (existing) {
                savedWing = existing.querySelector('.cart-upsells');
              }
            }

            /* Run original replacement */
            origUpdate.call(SectionDynamicUpdate, sections, responseSections);

            /* Re-insert the saved wing, replacing the fresh one from server */
            if (savedWing && window.innerWidth > 1024) {
              var container = document.getElementById('Cart-Drawer-Details');
              if (container) {
                var freshWing = container.querySelector('.cart-upsells');
                if (freshWing) {
                  freshWing.replaceWith(savedWing);
                }
              }
              /* Sync wing visibility with current cart contents */
              if (drawerEl._syncWingWithCart) drawerEl._syncWingWithCart();
            }
          };
          SectionDynamicUpdate._wingPatched = true;
        }

        /* ────────────────────────────────────────────────────────
           Desktop wing toggle — toggle button opens/closes the wing
           On mobile, the default collapsible behavior is preserved.
           ──────────────────────────────────────────────────────── */

        _bindWingToggle() {
          var self = this;

          /* Intercept the wing trigger button click on desktop */
          this.addEventListener('click', function(e) {
            var trigger = e.target.closest('.cart-wing-trigger');
            if (!trigger) return;
            if (window.innerWidth <= 1024) return; /* mobile uses default collapsible */

            e.preventDefault();
            e.stopPropagation();
            self._toggleWing();
          });
        }

        _toggleWing() {
          var wing = this.querySelector('.cart-upsells');
          if (!wing) return;

          var trigger = this.querySelector('.cart-wing-trigger');

          if (this._wingRevealed) {
            /* Close wing */
            wing.classList.remove('wing-animate', 'wing-revealed');
            this._wingRevealed = false;
            if (trigger) trigger.classList.remove('wing-open');
          } else {
            /* Open wing */
            this._syncWingWithCart();
            wing.classList.add('wing-animate');
            if (trigger) trigger.classList.add('wing-open');
            var self = this;
            setTimeout(function() {
              wing.classList.add('wing-revealed');
              self._wingRevealed = true;
            }, 850);
          }
        }

        /* ────────────────────────────────────────────────────────
           Wing ↔ Cart sync — hide upsell cards that are in the cart,
           show them again if removed.
           ──────────────────────────────────────────────────────── */

        _syncWingWithCart() {
          if (window.innerWidth <= 1024) return;
          var wing = this.querySelector('.cart-upsells');
          if (!wing) return;

          fetch('/cart.js?_fd=0', { credentials: 'same-origin' })
            .then(function(r) { return r.json(); })
            .then(function(cart) {
              /* Build a Set of variant IDs currently in the cart */
              var cartVariantIds = {};
              (cart.items || []).forEach(function(item) {
                cartVariantIds[Number(item.variant_id)] = true;
              });

              /* Show/hide each upsell card based on whether its variant is in the cart */
              var cards = wing.querySelectorAll('.cart-upsell-card');
              cards.forEach(function(card) {
                var btn = card.querySelector('.cart-upsell-add-btn');
                var vid = btn ? Number(btn.dataset.variantId) : 0;
                if (vid && cartVariantIds[vid]) {
                  card.style.display = 'none';
                } else {
                  /* Reset all inline styles from the add animation */
                  card.style.display = '';
                  card.style.opacity = '';
                  card.style.maxHeight = '';
                  card.style.overflow = '';
                  card.style.padding = '';
                  card.style.margin = '';
                  card.style.transition = '';
                  /* Re-enable the button */
                  if (btn) { btn.disabled = false; btn.textContent = '+ ADD'; }
                }
              });
            })
            .catch(function() {});
        }

        /* ────────────────────────────────────────────────────────
           Upsell +ADD and Heart save-for-later handlers
           Registered on `this` (the cart-drawer element) because
           ModalDialog.stopPropagation() blocks document-level delegation.
           ──────────────────────────────────────────────────────── */

        _bindCartActions() {
          var self = this;

          /* ── Checkout button click ── */
          this.addEventListener('click', function(e) {
            var checkoutBtn = e.target.closest('.cart-checkout-btn, [name="checkout"]');
            if (!checkoutBtn) return;

            /* Grab cart value for the event */
            var subtotalEl = self.querySelector('.cart-footer-value');
            var subtotal = subtotalEl ? subtotalEl.textContent.trim() : '';
            trackEvent('checkout_clicked', subtotal);
          });

          /* ── Trash icon (minus at qty 1) = remove item ── */
          this.addEventListener('click', function(e) {
            var minusBtn = e.target.closest('[name="minus"]');
            if (!minusBtn) return;

            /* Only intercept if the SVG trash icon is showing (qty is 1) */
            if (!minusBtn.querySelector('svg')) return;

            var qtyContainer = minusBtn.closest('quantity-input, .cart-item-qty');
            var input = qtyContainer?.querySelector('input[name="updates[]"]');
            if (!input) return;

            var val = parseInt(input.value) || 1;
            var min = parseInt(input.min) || 1;
            if (val > min) return; /* Not at minimum — let normal stepDown handle it */

            e.preventDefault();
            e.stopImmediatePropagation();

            /* Remove item: set quantity to 0 */
            var index = input.dataset.index;
            if (!index) return;

            var cartItemsEl = self.querySelector('cart-items');
            var sections = [];
            if (cartItemsEl?.getSectionsToRender) {
              sections = cartItemsEl.getSectionsToRender().map(function(s) { return s.section; });
            }

            var variantId = input.dataset.quantityVariantId || '';
            trackEvent('item_removed', variantId);

            fetch(window.routes?.cart_change_url || '/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ line: parseInt(index), quantity: 0, sections: sections, sections_url: window.location.pathname })
            })
            .then(function(r) { return r.json(); })
            .then(function(data) {
              if (cartItemsEl?.handleCartUpdate) {
                cartItemsEl.handleCartUpdate(data, parseInt(index), 0);
              }
              if (typeof publish === 'function') publish(PUB_SUB_EVENTS?.cartUpdate || 'cart-update', { cartData: data });
            });
          });

          /* ── Upsell "+ADD" ── */
          /* NOTE: Inline onclick on the button handles the actual add.
             This delegated handler is a fallback but must skip if inline already ran.
             The inline handler disables the button, so check that. */
          this.addEventListener('click', function(e) {
            var addBtn = e.target.closest('.cart-upsell-add-btn') || e.target.closest('[data-upsell-add]');
            if (!addBtn) return;

            /* Skip if inline onclick already handled this (button is disabled or has spinner) */
            if (addBtn.disabled) return;

            var card = addBtn.closest('[data-upsell-add]') || addBtn;
            var variantId = card.dataset?.variantId || addBtn.dataset?.variantId;
            if (!variantId) return;

            e.preventDefault();
            var btn = card.querySelector('.cart-upsell-add-btn') || addBtn;
            var originalText = btn.textContent;
            btn.textContent = '...';
            btn.disabled = true;

            fetch(window.routes?.cart_add_url || '/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: JSON.stringify({ items: [{ id: parseInt(variantId), quantity: 1 }] })
            })
            .then(function(r) { return r.json(); })
            .then(function(data) {
              if (data.status) throw new Error(data.description || 'Could not add item');
              btn.textContent = '\u2713';
              setTimeout(function() { btn.textContent = originalText; btn.disabled = false; }, 2000);
              if (window.DYCToast) {
                DYCToast.show({ type: 'cart', title: (data.items?.[0]?.title || 'Item') + ' added', desc: 'Cart updated' });
              }
              var upsellTitle = btn.dataset?.productTitle || card.querySelector('.cart-upsell-title')?.textContent?.trim() || variantId;
              var upsellHandle = btn.dataset?.productHandle || '';
              var upsellPrice = btn.dataset?.price || '';
              if (window.DYCAnalytics?.trackCustom) DYCAnalytics.trackCustom('cart', 'upsell_added', upsellTitle, { variant_id: variantId, handle: upsellHandle, price: upsellPrice });
              /* Refresh cart section but preserve the desktop wing */
              var ts = Date.now();
              fetch('/cart?section_id=' + (self.dataset?.section || '') + '&t=' + ts + '&_fd=0')
                .then(function(r) { return r.text(); })
                .then(function(html) {
                  var doc = new DOMParser().parseFromString(html, 'text/html');
                  var newDetails = doc.getElementById('Cart-Drawer-Details');
                  var existing = document.getElementById('Cart-Drawer-Details');
                  if (newDetails && existing) {
                    /* Save existing wing before replacing */
                    var existingWing = existing.querySelector('.cart-upsells');
                    existing.innerHTML = newDetails.innerHTML;
                    /* Re-insert preserved wing (desktop only) */
                    if (existingWing && window.innerWidth > 1024) {
                      var freshWing = existing.querySelector('.cart-upsells');
                      if (freshWing) freshWing.replaceWith(existingWing);
                    }
                  }
                  if (window.lucide) lucide.createIcons();
                  /* Update cart tab count */
                  var newCount = doc.querySelector('.cart-tab-count');
                  var oldCount = self.querySelector('.cart-tab-count');
                  if (newCount && oldCount) oldCount.textContent = newCount.textContent;
                })
                .catch(function() {});
              /* Also update bubble */
              fetch('/cart?section_id=cart-icon-bubble&t=' + ts + '&_fd=0')
                .then(function(r) { return r.text(); })
                .then(function(html) {
                  var doc = new DOMParser().parseFromString(html, 'text/html');
                  var nb = doc.getElementById('Cart-Icon-Bubble');
                  var ob = document.getElementById('Cart-Icon-Bubble');
                  if (nb && ob) ob.innerHTML = nb.innerHTML;
                })
                .catch(function() {});
            })
            .catch(function(err) {
              btn.textContent = originalText;
              btn.disabled = false;
              if (window.DYCToast) DYCToast.show({ type: 'warning', title: 'Couldn\'t add item', desc: err.message || 'Out of stock' });
            });
          });

          /* ── Heart save-for-later ── */
          this.addEventListener('click', function(e) {
            var heart = e.target.closest('.cart-item-heart');
            if (!heart) return;

            e.preventDefault();
            var variantId = parseInt(heart.dataset.variantId);
            if (!variantId || !window.DYCWishlist) return;

            var isSaved = DYCWishlist.isItemSaved(variantId);

            if (isSaved) {
              DYCWishlist.removeItem(variantId);
              heart.classList.remove('cart-item-heart--saved');
              heart.classList.add('cart-item-heart--animating');
              setTimeout(function() { heart.classList.remove('cart-item-heart--animating'); }, 400);
            } else {
              var itemData = {
                productId: parseInt(heart.dataset.productId),
                variantId: variantId,
                handle: heart.dataset.handle,
                title: heart.dataset.title,
                variantTitle: heart.dataset.variantTitle,
                price: parseInt(heart.dataset.price),
                compareAtPrice: parseInt(heart.dataset.compareAt) || null,
                image: heart.dataset.image
              };

              var cartItem = heart.closest('.cart-item');
              var qtyInput = cartItem?.querySelector('input[name="updates[]"]');
              var index = qtyInput?.dataset?.index;
              var currentQty = parseInt(qtyInput?.value) || 1;

              DYCWishlist.saveItem(itemData);
              if (window.DYCAnalytics?.trackCustom) DYCAnalytics.trackCustom('cart', 'item_saved', itemData.handle);

              if (index) {
                var cartItemsEl = self.querySelector('cart-items');
                var sections = [];
                if (cartItemsEl?.getSectionsToRender) {
                  sections = cartItemsEl.getSectionsToRender().map(function(s) { return s.section; });
                }
                fetch(window.routes?.cart_change_url || '/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ line: parseInt(index), quantity: 0, sections: sections })
                })
                .then(function(r) { return r.json(); })
                .then(function(data) {
                  if (cartItemsEl?.handleCartUpdate) cartItemsEl.handleCartUpdate(data, parseInt(index), 0);
                  if (typeof publish === 'function') publish(PUB_SUB_EVENTS?.cartUpdate || 'cart-update', { cartData: data });
                });
              }

              if (window.DYCToast) {
                DYCToast.show({
                  type: 'heart',
                  title: 'Saved for later',
                  desc: itemData.title + (itemData.variantTitle ? ' \u00B7 ' + itemData.variantTitle : ''),
                  actionText: 'Undo',
                  action: function() {
                    DYCWishlist.removeItem(variantId);
                    fetch(window.routes?.cart_add_url || '/cart/add.js', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                      body: JSON.stringify({ items: [{ id: variantId, quantity: currentQty }] })
                    }).then(function() { self._refreshSections(); });
                  }
                });
              }
            }
          });
        }

        _refreshSections() {
          var self = this;
          var sectionId = this.dataset?.section;
          if (!sectionId) return;
          var timestamp = Date.now();

          /* Fetch cart drawer section — same pattern as accessories-drawer.js */
          fetch('/cart?section_id=' + sectionId + '&t=' + timestamp + '&_fd=0')
            .then(function(r) { return r.text(); })
            .then(function(html) {
              var parser = new DOMParser();
              var doc = parser.parseFromString(html, 'text/html');

              /* Update cart drawer details — preserve desktop wing */
              var newDetails = doc.getElementById('Cart-Drawer-Details');
              var existingDetails = document.getElementById('Cart-Drawer-Details');
              if (newDetails && existingDetails) {
                var existingWing = existingDetails.querySelector('.cart-upsells');
                existingDetails.innerHTML = newDetails.innerHTML;
                if (existingWing && window.innerWidth > 1024) {
                  var freshWing = existingDetails.querySelector('.cart-upsells');
                  if (freshWing) freshWing.replaceWith(existingWing);
                }
              }

              /* Also refresh the cart icon bubble */
              return fetch('/cart?section_id=cart-icon-bubble&t=' + timestamp + '&_fd=0');
            })
            .then(function(r) { return r.text(); })
            .then(function(html) {
              var parser = new DOMParser();
              var doc = parser.parseFromString(html, 'text/html');
              var newBubble = doc.getElementById('Cart-Icon-Bubble');
              var existingBubble = document.getElementById('Cart-Icon-Bubble');
              if (newBubble && existingBubble) {
                existingBubble.innerHTML = newBubble.innerHTML;
              }

              /* Re-init icons and publish update */
              if (window.lucide) lucide.createIcons();
              if (typeof publish === 'function') {
                publish(window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update', {});
              }
              /* Sync wing cards with current cart */
              self._syncWingWithCart();
            })
            .catch(function(err) {
              console.warn('[cart-drawer] _refreshSections failed:', err);
            });
        }

        /* ────────────────────────────────────────────────────────
           Mobile swipe-to-close
           ──────────────────────────────────────────────────────── */

        _bindSwipeToDismiss() {
          var self = this;
          var header = this.querySelector('.cart-drawer-header');
          var panel = this.querySelector('.cart-drawer-panel');
          if (!header || !panel) return;

          /* Avoid double-binding */
          if (header._swipeBound) return;
          header._swipeBound = true;

          header.addEventListener('touchstart', function (e) {
            self._touchStartY = e.touches[0].clientY;
            self._touchStartTime = Date.now();
            self._swipeDelta = 0;
            panel.style.transition = 'none';
          }, { passive: true });

          header.addEventListener('touchmove', function (e) {
            var currentY = e.touches[0].clientY;
            self._swipeDelta = currentY - self._touchStartY;

            /* Only allow downward drag */
            if (self._swipeDelta > 0) {
              panel.style.transform = 'translateY(' + self._swipeDelta + 'px)';
            }
          }, { passive: true });

          header.addEventListener('touchend', function () {
            var elapsed = Date.now() - self._touchStartTime;
            var velocity = elapsed > 0 ? self._swipeDelta / elapsed : 0;

            if (velocity > 0.5 || self._swipeDelta > 150) {
              /* Dismiss */
              panel.style.transition = 'transform 200ms ease-out';
              panel.style.transform = 'translateY(100%)';
              setTimeout(function () {
                self.hide();
              }, 200);
            } else {
              /* Snap back */
              panel.style.transition = 'transform 200ms ease-out';
              panel.style.transform = '';
            }
          }, { passive: true });
        }
      }
    );
  }
})();
